import {
  BLOCKED_TYPE_TEXT,
  BlockedCheaterFields,
  BlockedCourseChipGroup,
  calculateBlockedDuration,
  useGetBlockedRecordDownloadSignedUrlWithInfo,
} from '@exam-center/blocked-user-management';
import { FormLeavingListener } from '@exam-center/components';
import {
  BlockedRecordBlockedScope,
  GetBlockedRecordByBlockedUserIdDocument,
  GetBlockedUserByIdDocument,
  useUpdateBlockedRecordMutation,
} from '@exam-center/graphql';
import { Flex, Form, FormButton, Text } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { dayjs, formatDate } from '@shared/utils';
import { useEffect, useMemo, useState } from 'react';

import {
  type BlockedRecordCheaterFormType,
  useBlockedRecordDetails,
  useBlockedRecordCheaterForm,
  BLOCKED_RECORD_CHEATER_FORM_FIELD_NAME,
} from '../../providers';

import { getFilePreviewModeByType } from './utils';

export default function BlockedRecordCheaterFormLogic() {
  const { showPromiseSnackbar } = useSnackbar();

  const { blockedRecord, blockedUser } = useBlockedRecordDetails();
  const formContext = useBlockedRecordCheaterForm();

  const [updateBlockedRecord, { loading: isUpdating }] = useUpdateBlockedRecordMutation({
    refetchQueries: [
      GetBlockedRecordByBlockedUserIdDocument,
      {
        query: GetBlockedUserByIdDocument,
        variables: {
          input: {
            id: blockedUser.id,
          },
        },
      },
    ],
  });

  const [blockedDuration, setBlockedDuration] = useState<string>(
    calculateBlockedDuration(blockedRecord.blockedAt, blockedRecord.blockedUntil ?? new Date()),
  );
  const [fieldVisibilityStatus, setFieldVisibilityStatus] = useState<{
    enforcementEndDuration: boolean;
  }>({
    enforcementEndDuration: !!blockedRecord.blockedUntil,
  });
  const blockedCourses = blockedRecord.blockedRecordCourses.map((course) => ({
    code: course.examCourse?.code ?? '',
    name: course.examCourse?.name ?? '',
  }));

  const { metadata } = blockedRecord;

  const isBlockedAllScope = blockedRecord.blockedScope === BlockedRecordBlockedScope.AllScope;

  const examStartedAt = dayjs(metadata.examSession?.startedAt);
  const examEndedAt = dayjs(metadata.examSession?.endedAt);

  const round = metadata.examSession?.roundNumber;
  const examDate = formatDate(metadata.examSession?.startedAt);
  const startTime = examStartedAt.format('HH:mm');
  const endTime = examEndedAt.format('HH:mm');

  const documentStorageObjects = useMemo(() => {
    return (
      blockedRecord.metadata.documentStorageObjects?.map((object) => ({
        fileName: object.fileName,
        key: object.key,
      })) ?? []
    );
  }, [blockedRecord.metadata.documentStorageObjects]);

  const evidenceFiles = useGetBlockedRecordDownloadSignedUrlWithInfo({
    blockedRecordId: blockedRecord.id,
    blockedRecordStorageObjects: documentStorageObjects,
    options: { skip: !blockedRecord },
  });

  const handleUpdateBlockedRecord = async (data: BlockedRecordCheaterFormType) => {
    await updateBlockedRecord({
      variables: {
        input: {
          blockedRecordId: blockedRecord.id,
          info: {
            blockedReason: data.remark,
            blockedUntil: data.enforcementEnd?.date,
          },
        },
      },
    });
  };

  const examCenterName = useMemo(() => {
    return blockedRecord.examCenter?.code && blockedRecord.examCenter.nameTH
      ? `${blockedRecord.examCenter?.code}: ${blockedRecord.examCenter.nameTH}`
      : '-';
  }, [blockedRecord.examCenter]);

  const handleSubmit = async (data: BlockedRecordCheaterFormType) => {
    await showPromiseSnackbar({
      message: {
        pending: 'กำลังบันทึกข้อมูล',
        success: 'บันทึกข้อมูลเรียบร้อยแล้ว',
        error: 'บันทึกข้อมูลไม่สำเร็จ',
      },
      promise: () => handleUpdateBlockedRecord(data),
    });
  };

  useEffect(() => {
    // REMARK: handle clear value
    const subscription = formContext.watch((value, { name }) => {
      // REMARK: handle clear value when enforcementEnd assign changes
      if (name === 'enforcementEnd.assign') {
        if (value.enforcementEnd?.assign === 'false') {
          formContext.resetField('enforcementEnd.date');
        }
      }

      // REMARK: handle calculate blockedUntil when enforcementEnd date changes
      if (name === 'enforcementEnd.date') {
        if (value.enforcementEnd?.date) {
          setBlockedDuration(
            calculateBlockedDuration(blockedRecord.blockedAt, value.enforcementEnd.date),
          );
        } else {
          setBlockedDuration('-');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext, blockedRecord.blockedAt]);

  useEffect(() => {
    const subscription = formContext.watch((value, { name }) => {
      if (name === 'enforcementEnd.assign') {
        setFieldVisibilityStatus((prev) => ({
          ...prev,
          enforcementEndDuration: value?.enforcementEnd?.assign === 'true',
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [formContext]);

  return (
    <>
      <Form
        onSubmit={formContext.handleSubmit(handleSubmit)}
        sx={(theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(6) })}
      >
        <BlockedCheaterFields
          fields={{
            blockedType: {
              mode: 'view',
              value: BLOCKED_TYPE_TEXT[blockedRecord?.blockedType],
            },
            blockedCourseType: {
              mode: 'view',
              value: isBlockedAllScope ? (
                <Text>ระงับสิทธิ์ทุกหลักสูตร</Text>
              ) : (
                <BlockedCourseChipGroup examCourseInfos={blockedCourses} />
              ),
              slotProps: {
                icon: { sx: { display: 'none' } },
              },
            },
            startedAt: {
              mode: 'view',
              value: formatDate(blockedRecord.blockedAt),
            },
            endedAt: {
              mode: 'input',
              assign: {
                name: 'enforcementEnd.assign',
              },
              date: {
                name: 'enforcementEnd.date',
                required: true,
                hidden: !fieldVisibilityStatus.enforcementEndDuration,
              },
            },
            remark: {
              mode: 'input',
              name: BLOCKED_RECORD_CHEATER_FORM_FIELD_NAME.remark,
            },
            examDate: {
              mode: 'view',
              value: examDate,
            },
            examRound: {
              mode: 'view',
              value: String(round),
            },
            examStartTime: {
              mode: 'view',
              value: startTime,
            },
            examEndTime: {
              mode: 'view',
              value: endTime,
            },
            examCenter: {
              mode: 'view',
              value: examCenterName,
            },
            evidenceFiles: {
              mode: 'view',
              slotProps: {
                files: evidenceFiles.map((file) => ({
                  ...file,
                  mode: getFilePreviewModeByType(file.type),
                })),
              },
            },
          }}
          options={{
            shouldDisplayEndedAtAsDate: true,
            shouldDisplayBlockedCourseSelection: false,
            shouldDisplayExamCenterSelection: true,
          }}
          slots={{
            endedAtDateEndAdornment: (
              <Text variant='body2' sx={(theme) => ({ alignSelf: 'end', pb: theme.spacing(3) })}>
                (รวมระยะเวลาระงับสิทธิ์ทั้งหมด: {blockedDuration})
              </Text>
            ),
            blockedAtLabelText: 'วันที่เริ่มระงับสิทธิ์',
            blockedUntilLabelText: 'วันที่สิ้นสุดระงับสิทธิ์',
          }}
        />
        <Flex sx={{ flex: 1, justifyContent: 'flex-end' }}>
          <FormButton disabled={isUpdating} label='บันทึก' />
        </Flex>
      </Form>
      <FormLeavingListener onSubmit={handleSubmit} />
    </>
  );
}
